import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReportRecordsTable from '../components/ReportRecordsTable';
import ReportActionModal from '../components/ReportActionModal';
import { Button } from '../components/adminModals';
import { useNotification } from '../../sharedComponents/Notifications';
import { useAuth } from '../../../auth/hooks/auth';
import {
    reportBatchService,
    ReportBatchItem,
    ApprovalReportData,
    APPROVAL_TYPE_LABELS,
    BATCH_STATUS_LABELS,
    BatchStatus,
} from '../services/adminAuth';
import { ArrowLeft, Pencil, SendHorizontal } from 'lucide-react';

// Mirrors the status colors used in the report batches table.
const batchStatusStyle = (status: number): string => {
    switch (status) {
        case BatchStatus.Draft: return 'bg-gray-100 text-gray-700';
        case BatchStatus.PendingApproval: return 'bg-yellow-50 text-yellow-700';
        case BatchStatus.Approved: return 'bg-green-50 text-green-700';
        case BatchStatus.ReadyForSubmission: return 'bg-blue-50 text-blue-700';
        case BatchStatus.Rejected: return 'bg-red-50 text-red-700';
        default: return 'bg-gray-100 text-gray-700';
    }
};

function ViewBatch() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const batch = (location.state as { batch?: ReportBatchItem } | null)?.batch ?? null;
    const { showNotification } = useNotification();
    const { user } = useAuth();

    // The TAG Chairman can build drafts and mark them ready, but not submit for approval.
    const isTagChairman = user?.roleId === 5;

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ApprovalReportData | null>(null);
    const [submitOpen, setSubmitOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const goToUpdate = () => batch && navigate(`/admin/approval-requests/${batch.id}/update`, { state: { batch } });

    const handleSubmit = async () => {
        if (!id) return;
        setSubmitting(true);
        try {
            const res = await reportBatchService.submitBatchForApproval(id);
            if (res.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Batch submitted for approval successfully', timer: 3000 });
                setSubmitOpen(false);
                navigate('/admin/approval-requests');
            } else {
                showNotification('error', { title: 'Error', message: res.message || 'Failed to submit batch for approval' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while submitting the batch' });
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const fetchItems = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await reportBatchService.getReportBatchItems(id);
                if (response.isSuccess) {
                    setData(response.data);
                } else {
                    showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch batch items' });
                }
            } catch (err) {
                showNotification('error', { title: 'Error', message: 'An error occurred while fetching batch items' });
            } finally {
                setLoading(false);
            }
        };
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">
                    <button
                        onClick={() => navigate('/admin/approval-requests')}
                        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Back to Approval Requests
                    </button>

                    <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                {batch?.title || APPROVAL_TYPE_LABELS[batch?.reportType ?? -1] || 'Batch Details'}
                            </h1>
                            <div className="flex flex-col items-start gap-1.5 text-sm text-gray-500">
                                {batch?.generatedBy && <span><span className="font-semibold text-gray-700">Generated by:</span> {batch.generatedBy}</span>}
                                <div className="flex items-center gap-3">
                                    {batch?.periodLabel && <span>{batch.periodLabel}</span>}
                                    {batch && (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${batchStatusStyle(batch.status)}`}>
                                            {BATCH_STATUS_LABELS[batch.status] || 'Unknown'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {batch && batch.status === BatchStatus.Draft && (
                            <Button variant="white" onClick={goToUpdate} className="w-full lg:w-auto flex items-center justify-center gap-2">
                                <Pencil size={16} /> Edit Draft
                            </Button>
                        )}
                        {batch && batch.status === BatchStatus.ReadyForSubmission && !isTagChairman && (
                            <Button variant="success" onClick={() => setSubmitOpen(true)} className="w-full lg:w-auto flex items-center justify-center gap-2">
                                <SendHorizontal size={16} /> Submit for Approval
                            </Button>
                        )}
                    </div>

                    <div className="bg-white rounded-[8px] shadow-[0_1px_3px_rgba(0,0,0,0.1)] px-[16px] sm:px-[24px] py-[16px] sm:py-[20px]">
                        <div className="mb-4">
                            <h2 className="text-[16px] sm:text-[18px] font-[700] text-[#0A0A0A] m-0 mb-1">Records</h2>
                            <p className="text-xs sm:text-sm text-gray-500">
                                {data ? `${data.totalRecords} record${data.totalRecords === 1 ? '' : 's'} in this batch` : 'Loading records…'}
                            </p>
                        </div>
                        <ReportRecordsTable data={data} loading={loading} reportType={batch?.reportType} emptyMessage="No records in this batch." />
                    </div>

                    <ReportActionModal
                        isOpen={submitOpen}
                        mode="submit"
                        onClose={() => setSubmitOpen(false)}
                        onConfirm={handleSubmit}
                        isSubmitting={submitting}
                        title={batch?.title || APPROVAL_TYPE_LABELS[batch?.reportType ?? -1] || 'Report Batch'}
                        subtitle={batch?.periodLabel}
                        records={data?.totalRecords}
                    />
                </div>
            </div>
        </div>
    );
}

export default ViewBatch;
